import { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { Loader2 } from 'lucide-react'

const Button = forwardRef(({ 
  className = '', 
  variant = 'primary', 
  size = 'md',
  loading = false,
  disabled = false,
  children,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  ...props 
}, ref) => {
  
  // Helper function to combine classes
  const cn = (...classes) => classes.filter(Boolean).join(' ')
  
  // Base styles
  const base = "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed select-none"
  
  // Size variants
  const sizes = {
    sm: "px-3 py-1.5 text-sm h-8",
    md: "px-4 py-2 text-sm h-10", 
    lg: "px-6 py-3 text-base h-12"
  }
  
  // Variant styles
  const variants = {
    primary: "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-sm hover:shadow focus:ring-orange-200",
    secondary: "bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-900 focus:ring-gray-200",
    outline: "border border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700 focus:ring-gray-200",
    danger: "bg-red-500 hover:bg-red-600 active:bg-red-700 text-white shadow-sm hover:shadow focus:ring-red-200",
    success: "bg-green-500 hover:bg-green-600 active:bg-green-700 text-white shadow-sm hover:shadow focus:ring-green-200",
    ghost: "hover:bg-gray-100 active:bg-gray-200 text-gray-700 focus:ring-gray-200",
    link: "text-orange-500 hover:text-orange-600 underline-offset-4 hover:underline focus:ring-orange-200"
  }
  
  // Icon sizes
  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-4 h-4", 
    lg: "w-5 h-5"
  }
  
  const isDisabled = disabled || loading
  
  return (
    <button
      ref={ref}
      disabled={isDisabled}
      className={cn(
        base,
        sizes[size],
        variants[variant],
        className
      )}
      {...props}
    >
      {/* Loading spinner */}
      {loading && (
        <Loader2 className={cn(
          "animate-spin",
          iconSizes[size],
          children && "mr-2"
        )} />
      )}
      
      {/* Left icon */}
      {!loading && LeftIcon && (
        <LeftIcon className={cn(
          iconSizes[size],
          children && "mr-2"
        )} />
      )}
      
      {/* Button text */}
      {children}
      
      {/* Right icon */}
      {!loading && RightIcon && (
        <RightIcon className={cn(
          iconSizes[size], 
          children && "ml-2"
        )} />
      )}
    </button>
  )
})

Button.displayName = 'Button'

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'danger', 'success', 'ghost', 'link']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  leftIcon: PropTypes.elementType,
  rightIcon: PropTypes.elementType,
}

export { Button }